import React, { useState, useEffect } from 'react';
import { UsersIcon, CheckCircleIcon, ClockIcon, PencilIcon } from '@heroicons/react/24/outline';
import { ModerationQueueNew } from '../components/moderation/ModerationQueueNew';
import { UserRole } from '../types/admin.types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import toast from 'react-hot-toast';

interface ModerationStats {
    totalApplications: number;
    pendingModeration: number;
    approved: number;
    rejected: number;
    needsRevision: number;
    avgProcessingHours: number;
}

export const ModerationPageNew: React.FC = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState<ModerationStats | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        setIsLoadingStats(true);
        try {
            const response = await apiService.getModerationStats();
            if (response.data.success) {
                setStats(response.data.data);
            } else {
                toast.error('Ошибка загрузки статистики');
            }
        } catch (error) {
            console.error('Ошибка загрузки статистики:', error);
            toast.error('Ошибка загрузки статистики');
        } finally {
            setIsLoadingStats(false);
        }
    };

    const StatCard: React.FC<{
        title: string;
        value: number;
        icon: React.ReactNode;
        color: string;
        bgColor: string;
    }> = ({ title, value, icon, color, bgColor }) => (
        <div className={`${bgColor} rounded-lg p-6 border border-gray-200`}>
            <div className="flex items-center">
                <div className={`flex-shrink-0 ${color}`}>
                    {icon}
                </div>
                <div className="ml-5 w-0 flex-1">
                    <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
                        <dd className="text-3xl font-bold text-gray-900">
                            {isLoadingStats ? (
                                <div className="animate-pulse bg-gray-300 h-8 w-16 rounded"></div>
                            ) : (
                                value.toLocaleString()
                            )}
                        </dd>
                    </dl>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Модерация клиентов</h1>
                <p className="mt-2 text-gray-600">
                    Управление заявками на регистрацию в системе "AISER"
                </p>
            </div>

            {/* Статистика */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                    title="Всего заявок"
                    value={stats?.totalApplications || 0}
                    icon={<UsersIcon className="h-8 w-8" />}
                    color="text-blue-500"
                    bgColor="bg-white"
                />
                <StatCard
                    title="На модерации"
                    value={stats?.pendingModeration || 0}
                    icon={<ClockIcon className="h-8 w-8" />}
                    color="text-yellow-500"
                    bgColor="bg-white"
                />
                <StatCard
                    title="На доработке"
                    value={stats?.needsRevision || 0}
                    icon={<PencilIcon className="h-8 w-8" />}
                    color="text-orange-500"
                    bgColor="bg-white"
                />
                <StatCard
                    title="Одобрено"
                    value={stats?.approved || 0}
                    icon={<CheckCircleIcon className="h-8 w-8" />}
                    color="text-green-500"
                    bgColor="bg-white"
                />
            </div>

            {/* Дополнительная статистика */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Производительность</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Среднее время обработки</span>
                            <span className="text-sm font-medium text-gray-900">
                                {isLoadingStats ? (
                                    <div className="animate-pulse bg-gray-300 h-4 w-16 rounded"></div>
                                ) : (
                                    `${stats?.avgProcessingHours?.toFixed(1) || 0} ч`
                                )}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Отклонено</span>
                            <span className="text-sm font-medium text-red-600">
                                {isLoadingStats ? (
                                    <div className="animate-pulse bg-gray-300 h-4 w-16 rounded"></div>
                                ) : (
                                    stats?.rejected || 0
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Быстрые действия</h3>
                    <div className="space-y-2">
                        <button
                            onClick={loadStats}
                            className="w-full btn-secondary justify-center"
                            disabled={isLoadingStats}
                        >
                            {isLoadingStats ? 'Обновление...' : 'Обновить статистику'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Очередь модерации */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Очередь модерации</h2>
                <ModerationQueueNew userRole={user?.role as UserRole || UserRole.MODERATOR} />
            </div>
        </div>
    );
};
